/**
 * WHAT THE CART WILL BE CHARGED — for the drawer, its lines and the sticky bar.
 *
 * Two sources, with one of them in charge:
 *
 *  - THE SERVER'S QUOTE for the basket (`quoteProductCart` → the public
 *    `apply-campaigns` route, the same `CampaignsService.applyCampaigns` the
 *    checkout quote and the order use). It is the total. Only it knows what a
 *    whole basket earns: a FIXED amount taken once, a minimum spend reached by
 *    three lines together, the priority a STACK is decided on across the cart.
 *    Asked when the basket changes — debounced, and a newer basket aborts the
 *    older question.
 *
 *  - THE BASKET RULE (`campaignBasketPricing`), the server's own rule run
 *    here, while the quote is on its way or if it fails — so a fixed amount off
 *    the whole shop comes off once and a minimum spend two lines reach together
 *    counts.
 *
 * EACH LINE is what THAT LINE is charged (UX-FINAL-2): the server's figure for
 * it when the quote says (`lineItems[].discount`, its share of a once-per-order
 * amount included), else the basket rule's figure for it. Never the product
 * card's price one line at a time: a card never bakes in a once-per-order
 * amount, and priced line by line "₪40 off a watches order" came off every
 * ₪30 strap — "0 ₪ ×2" on a ₪20 order. So the lines always add up to the
 * total. A line's unit price is given only when the line divides into whole
 * agorot (`unitPrice: null` otherwise — three straps for ₪50 are not 16.67
 * each); the line total is always given.
 *
 * A line's list price is read, in order, off the live catalogue row, off the
 * quote's own line, or off the cart — and off the cart only when the line is
 * marked as holding a list price (`snapshot.priceBasis`). An unmarked line may
 * be a figure a campaign was already taken off, so it is shown as it is and
 * never discounted a second time.
 *
 * A LINE THE SHOP NO LONGER SELLS is the server's to name, not this file's. The
 * quote refuses a basket holding one (400 ITEM_UNAVAILABLE, with the variant
 * ids); that refusal is an answer, not a failed request. The lines it names are
 * marked `unavailable`, priced at nothing and left out of the total, and the
 * rest of the basket is asked again — so the total stays the server's, and the
 * drawer never stands in the cached price of something the checkout would
 * refuse. A marked line stays marked while it is in the cart; a changed basket
 * is asked whole again, so one the shop sells again comes back.
 */
import { useEffect, useMemo, useState } from 'react';
import { quoteProductCart } from '../api/storeApi';
import { campaignBasketPricing, campaignLinePricing } from '../utils/shopPromotions';
import { effectiveVariantPrice as variantListPrice } from '../utils/productPrice';
import { isAskProduct } from '../utils/orderMode';
import useShopCampaigns from './useShopCampaigns';

export const QUOTE_DEBOUNCE_MS = 250;
const LIST = 'list';

const basketKey = (items) => (items ?? [])
  .map((i) => `${i?.variantId}:${i?.quantity}`)
  .join(',');

const refusedIds = (err, code) => {
  const data = err?.response?.data;
  if (!data || typeof data !== 'object') return null;
  const body = data.message && typeof data.message === 'object' && data.message.errorCode ? data.message : data;
  if (body?.errorCode !== code) return null;
  const ids = (Array.isArray(body.productVariantIds) ? body.productVariantIds : []).filter((id) => typeof id === 'string' && id);
  return ids.length > 0 ? ids : null;
};

/**
 * The variants a quote refused because the shop does not sell them — the ids of
 * a 400 ITEM_UNAVAILABLE, wherever the code sits (top level, or nested at
 * `message` by the exception filter). Null for any other failure: that is a
 * request that did not work, and says nothing about the basket.
 */
export function unavailableVariantIdsOf(err) {
  return refusedIds(err, 'ITEM_UNAVAILABLE');
}

/**
 * The variants a quote refused because they are ordered by ASKING the shop (400 ORDER_BY_ASKING: every refused line is
 * an "ask us" product; a mix comes back as ITEM_UNAVAILABLE and the live catalogue row tells the two apart). They
 * leave the total the same way, and the drawer says to ask the shop instead of "no longer available".
 */
export function askingVariantIdsOf(err) {
  return refusedIds(err, 'ORDER_BY_ASKING');
}

/**
 * Pure: price a cart from what is known right now. `quote` counts only when it
 * was asked for THIS basket (`quote.key === basketKey(items)`) and could price
 * it (a subtotal of 0 is "could not", never "free" — the checkout's own guard).
 *
 * `unavailable`: variant ids the server refused because the shop no longer sells
 * them (or sells them by asking). Those lines are marked, priced at nothing and
 * left out — the basket priced (and the quote matched) is the rest of it.
 * `asking`: the ones of those the server named ORDER_BY_ASKING; a line whose live
 * catalogue row is an ask product counts too (a mixed refusal names them all as
 * unavailable). A line marked `asking` says to ask the shop.
 */
export function priceCart({ items: allItems, products, campaigns, quote, unavailable, asking }) {
  const productById = new Map((products ?? []).filter(Boolean).map((p) => [p.id, p]));
  const refused = new Set(Array.isArray(unavailable) ? unavailable : []);
  const askingIds = new Set(Array.isArray(asking) ? asking : []);
  const unavailableLines = (allItems ?? []).filter((i) => refused.has(i?.variantId));
  const items = (allItems ?? []).filter((i) => !refused.has(i?.variantId));
  const key = basketKey(items);
  const fresh = !!quote && quote.key === key && Number(quote.subtotal) > 0;
  const quoteLine = new Map(
    fresh
      ? (quote.lineItems ?? [])
        .filter((l) => l?.itemType === 'PRODUCT_VARIANT')
        .map((l) => [l.itemId, l])
      : [],
  );

  // What is known about one line: its list unit price, whether that figure is
  // safe to discount, and the variant/product the campaign rule reads.
  const resolveLine = (item) => {
    const qty = Math.max(1, Number(item?.quantity) || 1);
    const product = productById.get(item?.productId) || null;
    const liveVariant = (product?.variants ?? []).find((v) => v?.id === item?.variantId) || null;
    const live = liveVariant ? variantListPrice(liveVariant) : null;
    const fromQuote = quoteLine.get(item?.variantId);
    let listUnit;
    let known = true;
    if (live != null) listUnit = live;
    else if (fromQuote && Number.isFinite(Number(fromQuote.price))) listUnit = Number(fromQuote.price) / qty;
    else {
      listUnit = Number(item?.snapshot?.price ?? 0) || 0;
      known = item?.snapshot?.priceBasis === LIST;
    }
    const variant = { ...(liveVariant || {}), id: item?.variantId, price: listUnit };
    const forRule = product || { id: item?.productId, categoryId: item?.snapshot?.categoryId ?? null };
    return { qty, listUnit, known, variant, forRule };
  };

  // What a product costs as a CARD — for a suggestion that is not in the basket.
  const cardPriceOf = (item) => {
    const { qty, listUnit, known, variant, forRule } = resolveLine(item);
    if (!known) {
      return { listUnit, unitPrice: listUnit, discounted: false, lineSubtotal: listUnit * qty, lineTotal: listUnit * qty, applied: [] };
    }
    const unit = campaignLinePricing(variant, forRule, campaigns ?? [], { quantity: 1 });
    const line = campaignLinePricing(variant, forRule, campaigns ?? [], { quantity: qty });
    return {
      listUnit,
      unitPrice: unit.total,
      discounted: unit.total < listUnit - 1e-9,
      lineSubtotal: line.subtotal,
      lineTotal: line.total,
      applied: line.applied,
    };
  };

  // The stand-in total: the lines whose list price is known, priced TOGETHER
  // as the server prices a basket; a line not known to hold a list price is
  // added as it is and never discounted again.
  const resolved = (items ?? []).map(resolveLine);
  const knownIndexes = [];
  resolved.forEach((r, i) => { if (r.known) knownIndexes.push(i); });
  const basket = campaignBasketPricing(
    knownIndexes.map((i) => ({ variant: resolved[i].variant, product: resolved[i].forRule, quantity: resolved[i].qty })),
    campaigns ?? [],
  );
  const basketLineOf = new Map(knownIndexes.map((itemIndex, k) => [itemIndex, basket.lines[k]]));
  const asIs = resolved.filter((r) => !r.known).reduce((sum, r) => sum + r.listUnit * r.qty, 0);
  const local = { subtotal: basket.subtotal + asIs, total: basket.total + asIs };
  const localNames = [];
  basket.applied.forEach((a) => {
    if (a?.campaign?.name && !localNames.includes(a.campaign.name)) localNames.push(a.campaign.name);
  });

  const agorot = (n) => Math.round((Number(n) || 0) * 100 + 1e-7);
  /** One basket line as shown: its list, what it comes to, and a unit price only when it multiplies back exactly. */
  const shownLine = (listUnit, qty, subtotalAgorot, totalAgorot, applied) => ({
    listUnit,
    unitPrice: totalAgorot % qty === 0 ? totalAgorot / qty / 100 : null,
    discounted: totalAgorot < subtotalAgorot,
    lineSubtotal: subtotalAgorot / 100,
    lineTotal: totalAgorot / 100,
    applied,
  });

  const lines = new Map((items ?? []).map((item, i) => {
    const r = resolved[i];
    if (!r.known) {
      return [item.variantId, {
        listUnit: r.listUnit, unitPrice: r.listUnit, discounted: false, lineSubtotal: r.listUnit * r.qty, lineTotal: r.listUnit * r.qty, applied: [],
      }];
    }
    const fromQuote = quoteLine.get(item?.variantId);
    if (fromQuote && fromQuote.discount != null && Number.isFinite(Number(fromQuote.discount)) && Number.isFinite(Number(fromQuote.price))) {
      const sub = agorot(fromQuote.price);
      return [item.variantId, shownLine(r.listUnit, r.qty, sub, sub - Math.min(agorot(fromQuote.discount), sub), [])];
    }
    const b = basketLineOf.get(i);
    return [item.variantId, shownLine(r.listUnit, r.qty, agorot(b.subtotal), agorot(b.total), basket.applied)];
  }));

  // A line the shop no longer sells: said so, and worth nothing to the total.
  for (const item of unavailableLines) {
    const listUnit = resolveLine(item).listUnit;
    const asks = askingIds.has(item.variantId) || isAskProduct(productById.get(item.productId));
    lines.set(item.variantId, {
      unavailable: true, ...(asks ? { asking: true } : {}), listUnit, unitPrice: null, discounted: false, lineSubtotal: 0, lineTotal: 0, applied: [],
    });
  }
  const unavailableIds = unavailableLines.map((i) => i.variantId);
  const askingLineIds = unavailableLines
    .filter((i) => lines.get(i.variantId)?.asking === true)
    .map((i) => i.variantId);

  /** A basket line as shown; anything else (a suggestion, another quantity) as its card. */
  const priceOf = (item) => {
    const current = (allItems ?? []).find((it) => it?.variantId === item?.variantId);
    if (current && Number(current.quantity) === Number(item?.quantity)) return lines.get(item.variantId);
    return cardPriceOf(item);
  };

  if (fresh) {
    return {
      source: 'server',
      subtotal: Number(quote.subtotal),
      discountTotal: Math.max(0, Number(quote.discountTotal) || 0),
      total: Math.max(0, Number(quote.total) || 0),
      campaignNames: (quote.appliedCampaigns ?? []).map((c) => c?.name).filter(Boolean),
      lines,
      priceOf,
      unavailable: unavailableIds,
      asking: askingLineIds,
    };
  }
  return {
    source: 'local',
    subtotal: local.subtotal,
    discountTotal: Math.max(0, local.subtotal - local.total),
    total: local.total,
    campaignNames: localNames,
    lines,
    priceOf,
    unavailable: unavailableIds,
    asking: askingLineIds,
  };
}

export default function useCartPricing(salonSlug, items, { active = true, products = null } = {}) {
  const campaigns = useShopCampaigns(salonSlug, { active });
  const key = basketKey(items);
  const [quote, setQuote] = useState(null);
  /** What the server refused, for which basket: `{ key, ids, asking }`. */
  const [refusal, setRefusal] = useState(null);

  // Marked while they are in the cart — even while a changed basket is being
  // asked again, so a refused line does not flash back in at its cached price.
  const inCart = new Set((items ?? []).map((i) => i?.variantId));
  const marked = (refusal?.ids ?? []).filter((id) => inCart.has(id));
  const markedKey = marked.join(',');
  const markedAsking = (refusal?.asking ?? []).filter((id) => inCart.has(id));
  const markedAskingKey = markedAsking.join(',');
  // The basket the server already refused those lines for is asked without
  // them; any other basket is asked whole, so a line sold again comes back.
  const confirmed = !!refusal && refusal.key === key;
  const askItems = (items ?? []).filter((i) => !(confirmed && marked.includes(i?.variantId)));
  const askKey = basketKey(askItems);

  useEffect(() => {
    if (!active || !salonSlug || !key || typeof quoteProductCart !== 'function') return undefined;
    const productItems = askItems
      .filter((i) => i?.variantId && Number(i.quantity) > 0)
      .map((i) => ({ productVariantId: i.variantId, quantity: Number(i.quantity) }));
    if (productItems.length === 0) return undefined;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    let dead = false;
    const whole = askKey === key;
    const timer = setTimeout(() => {
      Promise.resolve()
        .then(() => quoteProductCart(salonSlug, productItems, { signal: controller?.signal }))
        .then((data) => {
          if (dead || !data) return;
          setQuote({ key: askKey, ...data });
          // The whole basket priced: nothing in it is refused any more.
          if (whole) setRefusal(null);
        })
        .catch((err) => {
          if (dead) return;
          const asked = askingVariantIdsOf(err);
          const ids = asked || unavailableVariantIdsOf(err);
          // Refused lines are an answer about THIS basket; any other failure
          // leaves the display rule's sum standing in, as it always has.
          if (ids) {
            setRefusal({
              key,
              ids: [...new Set([...(whole ? [] : marked), ...ids])],
              asking: [...new Set([...(whole ? [] : markedAsking), ...(asked ?? [])])],
            });
          }
        });
    }, QUOTE_DEBOUNCE_MS);
    return () => {
      dead = true;
      clearTimeout(timer);
      controller?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salonSlug, key, askKey, active]);

  const pricing = useMemo(
    () => priceCart({
      items, products, campaigns, quote,
      unavailable: markedKey ? markedKey.split(',') : [],
      asking: markedAskingKey ? markedAskingKey.split(',') : [],
    }),
    [items, products, campaigns, quote, markedKey, markedAskingKey],
  );
  return { ...pricing, campaigns: campaigns ?? [] };
}
